import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { createDecks } from "./catchphraserDecks";

const CatchphraserCommunity = () => {
  const [activeDeck, setActiveDeck] = useState("overview");
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [paceMode, setPaceMode] = useState("guided");
  const intervalRef = useRef(null);

  const paceModes = {
    demo: { label: "Demo", multiplier: 0.65, description: "fast overview" },
    guided: { label: "Guided", multiplier: 1, description: "presentation pace" },
    reading: { label: "Reading", multiplier: 1.8, description: "self-paced viewing" },
  };

  const asset = (name) =>
    `${process.env.REACT_APP_USER_API_URL}/uploads/screens/${name}`;

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const LinkIcon = () => (
    <svg
      className="mr-1.5 h-3 w-3"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
      />
    </svg>
  );

  const ExternalLink = ({ compact = false }) => (
    <a
      href="https://catchphraser.com"
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center rounded-full border border-blue-200 bg-white/85 font-semibold text-blue-700 shadow-sm backdrop-blur-md transition-all hover:-translate-y-0.5 hover:bg-white hover:shadow-md ${
        compact ? "px-2.5 py-1 text-[11px]" : "px-3 py-1.5 text-xs"
      }`}
    >
      <LinkIcon />
      catchphraser.com
    </a>
  );

  const DesktopLink = () => (
    <div className="absolute right-5 top-5 z-20 hidden sm:block">
      <ExternalLink />
    </div>
  );

  const Pill = ({ children, color = "blue" }) => {
    const colors = {
      blue: "bg-blue-50 text-blue-700 ring-blue-200",
      purple: "bg-purple-50 text-purple-700 ring-purple-200",
      green: "bg-emerald-50 text-emerald-700 ring-emerald-200",
      pink: "bg-pink-50 text-pink-700 ring-pink-200",
      gray: "bg-gray-50 text-gray-700 ring-gray-200",
      amber: "bg-amber-50 text-amber-700 ring-amber-200",
      cyan: "bg-cyan-50 text-cyan-700 ring-cyan-200",
    };

    return (
      <span
        className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-semibold ring-1 ${colors[color]}`}
      >
        {children}
      </span>
    );
  };

  const SoftCard = ({ children, className = "" }) => (
    <div
      className={`rounded-3xl border border-white/70 bg-white/85 p-5 shadow-[0_18px_60px_rgba(15,23,42,0.08)] backdrop-blur-sm ${className}`}
    >
      {children}
    </div>
  );

  const EmojiCard = ({ emoji, title, text, className = "" }) => (
    <SoftCard className={`text-center ${className}`}>
      <div className="mb-3 text-3xl">{emoji}</div>
      <h4 className="mb-2 text-lg font-black text-gray-950">{title}</h4>
      {text && <p className="text-sm leading-relaxed text-gray-600">{text}</p>}
    </SoftCard>
  );

  const CleanList = ({ items, accent = "gray" }) => {
    const accents = {
      gray: "border-gray-200/70 text-gray-700",
      blue: "border-blue-100 text-gray-700",
      purple: "border-purple-100 text-gray-700",
      green: "border-emerald-100 text-gray-700",
      amber: "border-amber-100 text-gray-700",
      pink: "border-pink-100 text-gray-700",
    };

    return (
      <div className="space-y-0 text-left">
        {items.map((item) => (
          <div
            key={item}
            className={`border-b py-2.5 text-[15px] font-semibold leading-relaxed last:border-0 ${
              accents[accent] || accents.gray
            }`}
          >
            {item}
          </div>
        ))}
      </div>
    );
  };

  const SectionLabel = ({ children }) => (
    <div className="mx-auto mb-3 inline-flex items-center rounded-full border border-gray-200 bg-white/80 px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] text-gray-500 shadow-sm">
      {children}
    </div>
  );

  const ConceptMark = ({ emoji = "🎤", label = "Speech", sub = "practice" }) => (
    <div className="mx-auto mb-4 flex flex-col items-center">
      <div className="flex h-20 w-20 items-center justify-center rounded-[1.5rem] bg-gradient-to-br from-blue-600 to-purple-600 text-4xl shadow-[0_20px_50px_rgba(15,23,42,0.16)] ring-1 ring-white/50">
        {emoji}
      </div>
      <div className="mt-3 text-xs font-black uppercase tracking-[0.18em] text-gray-500">
        {label}
      </div>
      <div className="text-xs font-semibold text-gray-400">{sub}</div>
    </div>
  );

  const ScreenshotFrame = ({
    src,
    alt,
    caption,
    className = "",
    imgClassName = "",
  }) => (
    <div
      className={`rounded-[1.75rem] border border-gray-200/80 bg-white p-3 shadow-[0_22px_70px_rgba(15,23,42,0.11)] ${className}`}
    >
      <img
        src={src}
        alt={alt}
        className={`w-full rounded-[1.2rem] border border-gray-100 object-cover ${imgClassName}`}
      />
      {caption && (
        <div className="px-2 pt-2 text-center text-xs font-medium text-gray-500">
          {caption}
        </div>
      )}
    </div>
  );

  const NumberedList = ({ items, accent = "blue" }) => {
    const color =
      accent === "purple"
        ? "text-purple-600"
        : accent === "green"
        ? "text-emerald-600"
        : accent === "amber"
        ? "text-amber-600"
        : "text-blue-600";

    return (
      <div className="space-y-2.5 text-left">
        {items.map((item, index) => (
          <div key={index} className="flex items-start gap-3">
            <div
              className={`mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-white text-xs font-black shadow-sm ${color}`}
            >
              {index + 1}
            </div>
            <div className="text-[15px] leading-relaxed text-gray-700">
              {item}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const ComparisonTable = ({
    leftTitle,
    rightTitle,
    rows,
    leftColor = "blue",
    rightColor = "purple",
  }) => {
    const leftClasses =
      leftColor === "green"
        ? "from-emerald-50 to-white ring-emerald-200"
        : leftColor === "amber"
        ? "from-amber-50 to-white ring-amber-200"
        : "from-blue-50 to-white ring-blue-200";

    const rightClasses =
      rightColor === "pink"
        ? "from-pink-50 to-white ring-pink-200"
        : rightColor === "green"
        ? "from-emerald-50 to-white ring-emerald-200"
        : "from-purple-50 to-white ring-purple-200";

    return (
      <div className="grid w-full gap-5 md:grid-cols-2">
        <div
          className={`rounded-3xl bg-gradient-to-br p-5 shadow-sm ring-1 ${leftClasses}`}
        >
          <h4 className="mb-4 text-center text-xl font-black text-gray-900">
            {leftTitle}
          </h4>
          <NumberedList items={rows.map((row) => row[0])} accent={leftColor} />
        </div>

        <div
          className={`rounded-3xl bg-gradient-to-br p-5 shadow-sm ring-1 ${rightClasses}`}
        >
          <h4 className="mb-4 text-center text-xl font-black text-gray-900">
            {rightTitle}
          </h4>
          <NumberedList
            items={rows.map((row) => row[1])}
            accent={rightColor === "green" ? "green" : "purple"}
          />
        </div>
      </div>
    );
  };

  const BigStatement = ({ title, children, tone = "blue", compact = false }) => {
    const tones = {
      blue: "from-blue-100 via-white to-purple-100 border-blue-200",
      green: "from-emerald-100 via-white to-blue-100 border-emerald-200",
      purple: "from-purple-100 via-white to-pink-100 border-purple-200",
      amber: "from-amber-100 via-white to-orange-100 border-amber-200",
    };

    return (
      <div
        className={`rounded-3xl border bg-gradient-to-r shadow-[0_18px_50px_rgba(15,23,42,0.08)] ${
          compact ? "p-4" : "p-5"
        } ${tones[tone]}`}
      >
        <div className="mb-1 text-lg font-black text-gray-950">{title}</div>
        {children && (
          <div className="text-sm leading-relaxed text-gray-700 sm:text-base">
            {children}
          </div>
        )}
      </div>
    );
  };

  const Timeline = ({ steps }) => (
    <div className="grid gap-4 md:grid-cols-3">
      {steps.map(([emoji, label, title, text, tone]) => (
        <SoftCard key={label} className={`text-center shadow-none ring-1 ${tone}`}>
          <div className="mb-3 text-4xl">{emoji}</div>
          <div className="mb-2 text-xs font-black uppercase tracking-[0.18em] text-gray-500">
            {label}
          </div>
          <h4 className="mb-2 text-xl font-black text-gray-950">{title}</h4>
          <p className="text-sm leading-relaxed text-gray-600">{text}</p>
        </SoftCard>
      ))}
    </div>
  );

  const helpers = {
    ExternalLink,
    Pill,
    SoftCard,
    EmojiCard,
    CleanList,
    SectionLabel,
    ConceptMark,
    ScreenshotFrame,
    NumberedList,
    ComparisonTable,
    BigStatement,
    Timeline,
  };

  const decks = useMemo(() => createDecks({ helpers, asset }), []);
  const slides = decks[activeDeck].slides;

  const inferSlideDensity = useCallback(
    (slide, index) => {
      if (slide.density) return slide.density;
      if (activeDeck === "business" && index === slides.length - 1) return "team";
      return "medium";
    },
    [activeDeck, slides.length]
  );

  const getSlideDuration = useCallback(
    (slide, index = currentSlide) => {
      const densityDurations = {
        light: 7000,
        medium: 11000,
        dense: 15000,
        screenshot: 14000,
        team: 18000,
      };

      const density = inferSlideDensity(slide, index);
      const baseDuration =
        slide.duration || densityDurations[density] || densityDurations.medium;

      return Math.round(baseDuration * paceModes[paceMode].multiplier);
    },
    [currentSlide, inferSlideDensity, paceMode]
  );

  const stopSlideshow = useCallback(() => {
    if (intervalRef.current) clearTimeout(intervalRef.current);
    intervalRef.current = null;
    setIsPlaying(false);
  }, []);

  const nextSlide = useCallback(() => {
    setCurrentSlide((current) => Math.min(current + 1, slides.length - 1));
  }, [slides.length]);

  const prevSlide = useCallback(() => {
    setCurrentSlide((current) => Math.max(current - 1, 0));
  }, []);

  const goToSlide = useCallback((index) => {
    setCurrentSlide(index);
  }, []);

  const switchDeck = useCallback(
    (deckKey) => {
      setActiveDeck(deckKey);
      setCurrentSlide(0);
      stopSlideshow();
    },
    [stopSlideshow]
  );

  const playSlideshow = useCallback(() => {
    setIsPlaying((playing) => !playing);
  }, []);

  useEffect(() => {
    if (isMobile) {
      stopSlideshow();
    }
  }, [isMobile, stopSlideshow]);

  useEffect(() => {
    if (!isPlaying) {
      if (intervalRef.current) clearTimeout(intervalRef.current);
      intervalRef.current = null;
      return;
    }

    if (isMobile) return;

    if (intervalRef.current) clearTimeout(intervalRef.current);

    intervalRef.current = setTimeout(() => {
      setCurrentSlide((current) => {
        if (current < slides.length - 1) return current + 1;
        setIsPlaying(false);
        return current;
      });
    }, getSlideDuration(slides[currentSlide], currentSlide));

    return () => {
      if (intervalRef.current) clearTimeout(intervalRef.current);
    };
  }, [
    isPlaying,
    isMobile,
    currentSlide,
    activeDeck,
    paceMode,
    slides,
    getSlideDuration,
  ]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (isMobile) return;

      const tag = event.target?.tagName?.toLowerCase();
      if (tag === "input" || tag === "textarea" || event.target?.isContentEditable)
        return;

      switch (event.key) {
        case "ArrowLeft":
          event.preventDefault();
          prevSlide();
          break;
        case "ArrowRight":
        case " ":
          event.preventDefault();
          nextSlide();
          break;
        case "p":
        case "P":
          event.preventDefault();
          playSlideshow();
          break;
        case "Home":
          event.preventDefault();
          goToSlide(0);
          break;
        case "End":
          event.preventDefault();
          goToSlide(slides.length - 1);
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [goToSlide, nextSlide, playSlideshow, prevSlide, slides.length, isMobile]);

  useEffect(() => {
    return () => {
      if (intervalRef.current) clearTimeout(intervalRef.current);
    };
  }, []);

  const progress = ((currentSlide + 1) / slides.length) * 100;
  const activeDurationSeconds = Math.round(
    getSlideDuration(slides[currentSlide], currentSlide) / 1000
  );

  if (isMobile) {
    return (
      <div className="min-h-screen bg-slate-100 text-gray-900">
        <div className="sticky top-0 z-50 border-b border-slate-200 bg-white/95 px-3 py-3 shadow-sm backdrop-blur-md">
          <div className="mb-3 flex gap-2 overflow-x-auto pb-1">
            {Object.entries(decks).map(([key, deck]) => (
              <button
                key={key}
                onClick={() => switchDeck(key)}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-xs font-black transition-all ${
                  activeDeck === key
                    ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-md"
                    : "border border-gray-200 bg-white text-gray-600 shadow-sm"
                }`}
                title={deck.description}
              >
                {deck.label}
              </button>
            ))}
          </div>

          <div className="flex items-center justify-between gap-2">
            <div className="rounded-full border border-gray-200 bg-white px-3 py-1 text-xs font-bold text-gray-500 shadow-sm">
              {decks[activeDeck].label} • {slides.length} slides
            </div>

            <ExternalLink compact />
          </div>
        </div>

        <main className="mx-auto max-w-xl space-y-4 px-3 py-4">
          <section className="rounded-[1.75rem] bg-slate-950 p-5 text-white shadow-xl">
            <div className="text-xs font-black uppercase tracking-[0.2em] text-slate-400">
              mobile portrait mode
            </div>

            <h1 className="mt-2 text-2xl font-black">
              {decks[activeDeck].label}
            </h1>

            <p className="mt-2 text-sm leading-relaxed text-slate-300">
              The same presentation is shown as vertical cards, so it reads
              naturally on a phone.
            </p>
          </section>

          {slides.map((mobileSlide, index) => (
            <section
              key={`${activeDeck}-${index}`}
              className={`overflow-hidden rounded-[1.75rem] shadow-lg ring-1 ring-black/5 ${
                mobileSlide.bgColor || "bg-white"
              }`}
            >
              <div className="border-b border-black/5 bg-white/75 px-4 py-3 backdrop-blur-sm">
                <div className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">
                  Slide {index + 1}
                </div>

                {mobileSlide.title && (
                  <h2 className="mt-1 text-lg font-black leading-tight text-slate-950">
                    {mobileSlide.title}
                  </h2>
                )}
              </div>

              <div
                className="
                  px-3 py-5
                  [&_.absolute]:static
                  [&_.absolute]:mb-3
                  [&_.absolute]:flex
                  [&_.absolute]:justify-center
                  [&_.right-3]:right-auto
                  [&_.top-3]:top-auto
                  [&_.right-4]:right-auto
                  [&_.top-4]:top-auto
                  [&_.max-w-7xl]:max-w-full
                  [&_.max-w-6xl]:max-w-full
                  [&_.max-w-5xl]:max-w-full
                  [&_.max-w-4xl]:max-w-full
                  [&_.space-y-8]:space-y-5
                  [&_.space-y-7]:space-y-5
                  [&_.space-y-6]:space-y-4
                  [&_.space-y-5]:space-y-4
                  [&_.grid]:grid-cols-1
                  [&_.grid]:gap-4
                  [&_h1]:text-3xl
                  [&_h1]:leading-tight
                  [&_h2]:text-2xl
                  [&_h3]:text-2xl
                  [&_h4]:text-lg
                  [&_p]:text-sm
                  [&_p]:leading-relaxed
                  [&_.text-xl]:text-base
                  [&_.text-lg]:text-sm
                  [&_.text-4xl]:text-3xl
                  [&_.text-5xl]:text-3xl
                  [&_.text-6xl]:text-4xl
                  [&_.text-7xl]:text-4xl
                  [&_.p-5]:p-4
                  [&_.px-4]:px-2
                  [&_img]:max-h-none
                  [&_img]:object-contain
                "
              >
                {mobileSlide.content}
              </div>
            </section>
          ))}
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,_#e0f2fe,_transparent_28%),radial-gradient(circle_at_bottom_right,_#f3e8ff,_transparent_30%),linear-gradient(135deg,#f8fafc,#f1f5f9)] p-3 text-gray-900 sm:p-4 md:p-5">
      <div className="mx-auto w-full max-w-[1480px]">
        <div className="mb-3 flex flex-wrap justify-center gap-2">
          {Object.entries(decks).map(([key, deck]) => (
            <button
              key={key}
              onClick={() => switchDeck(key)}
              className={`rounded-full px-4 py-2 text-xs font-black transition-all sm:text-sm ${
                activeDeck === key
                  ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-md"
                  : "border border-gray-200 bg-white/80 text-gray-600 shadow-sm hover:-translate-y-0.5 hover:bg-white hover:shadow-md"
              }`}
              title={deck.description}
            >
              {deck.label}
            </button>
          ))}
        </div>

        <div className="mb-3 flex flex-col items-start justify-between gap-3 lg:flex-row lg:items-center">
          <div className="inline-flex items-center rounded-full border border-gray-200 bg-white/80 px-3 py-1 text-xs font-bold text-gray-500 shadow-sm backdrop-blur-sm sm:text-sm">
            {decks[activeDeck].label} • {currentSlide + 1} / {slides.length}
            {isPlaying && (
              <span className="ml-2 text-blue-600">
                • {activeDurationSeconds}s
              </span>
            )}
          </div>

          <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center lg:w-auto">
            <div className="flex rounded-full border border-gray-200 bg-white/80 p-1 shadow-sm backdrop-blur-sm">
              {Object.entries(paceModes).map(([key, mode]) => (
                <button
                  key={key}
                  onClick={() => setPaceMode(key)}
                  className={`rounded-full px-3 py-1.5 text-[11px] font-black transition-all sm:text-xs ${
                    paceMode === key
                      ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-md"
                      : "text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                  }`}
                  title={mode.description}
                >
                  {mode.label}
                </button>
              ))}
            </div>

            <button
              onClick={playSlideshow}
              className={`flex flex-1 items-center justify-center rounded-full px-4 py-2 text-xs font-black text-white shadow-lg transition-all hover:-translate-y-0.5 hover:shadow-xl sm:flex-none sm:text-sm ${
                isPlaying
                  ? "bg-gradient-to-r from-red-500 to-orange-500"
                  : "bg-gradient-to-r from-blue-600 to-purple-600"
              }`}
            >
              {isPlaying ? "Pause" : "Play"}
            </button>
          </div>
        </div>

        <div className="mb-4 h-2 overflow-hidden rounded-full bg-white/70 shadow-inner ring-1 ring-gray-200">
          <div
            className="h-full rounded-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>

        <div
          className={`relative mx-auto aspect-[16/9] w-full overflow-hidden rounded-[1.75rem] shadow-[0_30px_100px_rgba(15,23,42,0.14)] ring-1 ring-black/5 transition-all duration-300 sm:rounded-[2rem] ${slides[currentSlide].bgColor}`}
        >
          {currentSlide > 0 && <DesktopLink />}

          <div className="flex h-full flex-col p-4 sm:p-6 md:p-8 lg:p-10 xl:p-12">
            {slides[currentSlide].title && (
              <div className="mb-4 text-center">
                <h2 className="font-mono text-lg font-black tracking-tight text-gray-950 sm:text-xl md:text-2xl lg:text-3xl">
                  {slides[currentSlide].title}
                </h2>
                <div className="mx-auto mt-2 h-1 w-20 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 sm:w-28" />
              </div>
            )}

            <div className="flex flex-grow items-center overflow-hidden">
              <div className="w-full">{slides[currentSlide].content}</div>
            </div>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between gap-2">
          <button
            onClick={prevSlide}
            disabled={currentSlide === 0}
            className={`rounded-full px-4 py-2 text-xs font-bold transition sm:text-sm ${
              currentSlide === 0
                ? "cursor-not-allowed text-gray-400"
                : "bg-white/80 text-gray-700 shadow-sm hover:-translate-y-0.5 hover:bg-white hover:shadow-md"
            }`}
          >
            ← Previous
          </button>

          <div className="flex max-w-[55%] flex-wrap justify-center gap-1.5">
            {slides.map((_, index) => (
              <button
                key={`${activeDeck}-${index}`}
                onClick={() => goToSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
                className={`h-2 rounded-full transition-all ${
                  currentSlide === index
                    ? isPlaying
                      ? "w-6 animate-pulse bg-emerald-500"
                      : "w-6 bg-blue-600"
                    : "w-2 bg-gray-300 hover:bg-gray-400"
                }`}
              />
            ))}
          </div>

          <button
            onClick={nextSlide}
            disabled={currentSlide === slides.length - 1}
            className={`rounded-full px-4 py-2 text-xs font-bold transition sm:text-sm ${
              currentSlide === slides.length - 1
                ? "cursor-not-allowed text-gray-400"
                : "bg-white/80 text-gray-700 shadow-sm hover:-translate-y-0.5 hover:bg-white hover:shadow-md"
            }`}
          >
            {currentSlide === slides.length - 1 ? "Finish" : "Next →"}
          </button>
        </div>

        <div className="mt-3 text-center text-xs font-medium text-gray-400">
          ← → to navigate • Space to advance • P to play/pause
        </div>
      </div>
    </div>
  );
};

export default CatchphraserCommunity;